import React from "react";
import FeatherIcon from "feather-icons-react";
import {
  askEvivesomaAI,
  confirmEvivesomaAIAction,
  getAIErrorMessage,
  getAssistantText
} from "../../services/aiAssistant";

const SUGGESTIONS = {
  ask: ["Comment utiliser Evivesoma ?", "Explique-moi cette fonctionnalité."],
  assist: ["Combien avons-nous d’élèves ?", "Recherche un membre du personnel."],
  act: ["Prépare un message interne.", "Envoie un rappel à un membre du personnel."]
};

const MODES = [
  { id: "ask", label: "Question", icon: "message-circle" },
  { id: "assist", label: "Données", icon: "search" },
  { id: "act", label: "Action", icon: "zap" }
];

const MIN_MESSAGE_LENGTH = 2;
const MAX_MESSAGE_LENGTH = 4000;

const createMessage = (role, content, extra = {}) => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  role,
  content,
  ...extra
});

function EvivesomaAIPanel({ isOpen, onClose }) {
  const [mode, setMode] = React.useState("ask");
  const [input, setInput] = React.useState("");
  const [messages, setMessages] = React.useState([]);
  const [proposal, setProposal] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [isOnline, setIsOnline] = React.useState(() => navigator.onLine);
  const endRef = React.useRef(null);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isOpen, onClose]);

  React.useEffect(() => {
    const updateNetworkStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener("online", updateNetworkStatus);
    window.addEventListener("offline", updateNetworkStatus);
    return () => {
      window.removeEventListener("online", updateNetworkStatus);
      window.removeEventListener("offline", updateNetworkStatus);
    };
  }, []);

  React.useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, proposal, loading]);

  const apiHistory = messages
    .filter((item) => ["user", "assistant"].includes(item.role))
    .slice(-10)
    .map((item) => ({ role: item.role, content: item.content.slice(0, 4000) }));

  const send = async (event) => {
    event?.preventDefault();
    const message = input.trim();
    if (
      message.length < MIN_MESSAGE_LENGTH ||
      message.length > MAX_MESSAGE_LENGTH ||
      loading ||
      !isOnline
    ) return;

    const userMessage = createMessage("user", message);
    setMessages((items) => [...items, userMessage]);
    setInput("");
    setProposal(null);
    setLoading(true);

    try {
      const result = await askEvivesomaAI({
        mode,
        message,
        history: apiHistory
      });
      const text =
        getAssistantText(result) ||
        (result.requiresConfirmation
          ? "Cette action est prête et attend votre confirmation."
          : "La demande a été traitée.");
      setMessages((items) => [...items, createMessage("assistant", text)]);

      if (result.requiresConfirmation && result.confirmationToken) {
        setProposal({
          details: result.proposal || {},
          token: result.confirmationToken
        });
      }
    } catch (error) {
      setMessages((items) => [
        ...items,
        createMessage("error", getAIErrorMessage(error))
      ]);
    } finally {
      setLoading(false);
    }
  };

  const confirmAction = async () => {
    if (!proposal?.token || loading) return;
    setLoading(true);
    try {
      const result = await confirmEvivesomaAIAction(proposal.token);
      setMessages((items) => [
        ...items,
        createMessage(
          "assistant",
          getAssistantText(result) || "L’action a été exécutée avec succès."
        )
      ]);
      setProposal(null);
    } catch (error) {
      setMessages((items) => [
        ...items,
        createMessage("error", getAIErrorMessage(error))
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className={`ev-ai-scrim ${isOpen ? "is-open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`ev-ai-panel ${isOpen ? "is-open" : ""}`}
        aria-hidden={!isOpen}
        aria-label="Evivesoma IA"
      >
        <header className="ev-ai-panel__header">
          <span className="ev-ai-panel__logo"><FeatherIcon icon="cpu" size={21} /></span>
          <div>
            <strong>Evivesoma IA</strong>
            <small><i /> Assistant connecté</small>
          </div>
          <button type="button" onClick={onClose} aria-label="Fermer Evivesoma IA">
            <FeatherIcon icon="x" size={20} />
          </button>
        </header>

        <nav className="ev-ai-panel__modes" aria-label="Mode de l’assistant" style={{ display: "none" }}>
          {MODES.map((item) => (
            <button
              key={item.id}
              type="button"
              className={mode === item.id ? "is-active" : ""}
              aria-pressed={mode === item.id}
              disabled={loading}
              onClick={() => {
                setMode(item.id);
                setProposal(null);
              }}
            >
              <FeatherIcon icon={item.icon} size={14} />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="ev-ai-panel__messages" aria-live="polite">
          {messages.length === 0 && (
            <div className="ev-ai-panel__welcome">
              <span><FeatherIcon icon="sparkles" size={25} /></span>
              <h2>Bonjour, comment puis-je vous aider ?</h2>
              <p>
                {mode === "ask" && "Posez une question générale sur Evivesoma."}
                {mode === "assist" && "Consultez les données scolaires auxquelles vous avez accès."}
                {mode === "act" && "Préparez une action qui restera soumise à votre confirmation."}
              </p>
              <div>
                {SUGGESTIONS[mode].map((suggestion) => (
                  <button key={suggestion} type="button" onClick={() => setInput(suggestion)}>
                    {suggestion}<FeatherIcon icon="arrow-up-right" size={14} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((message) => (
            <div key={message.id} className={`ev-ai-bubble ev-ai-bubble--${message.role}`}>
              <span>
                <FeatherIcon
                  icon={message.role === "user" ? "user" : message.role === "error" ? "alert-circle" : "cpu"}
                  size={14}
                />
              </span>
              <div>
                <strong>{message.role === "user" ? "Vous" : "Evivesoma IA"}</strong>
                <p>{message.content}</p>
              </div>
            </div>
          ))}

          {proposal && (
            <section className="ev-ai-proposal">
              <header><FeatherIcon icon="shield" size={17} /><strong>Confirmation requise</strong></header>
              <p>{proposal.details.message || "Confirmez-vous l’exécution de cette action ?"}</p>
              {proposal.details.action && <small>Action : message interne</small>}
              {proposal.details.receiverIds?.length > 0 && (
                <small>
                  {proposal.details.receiverIds.length} destinataire
                  {proposal.details.receiverIds.length > 1 ? "s" : ""}
                </small>
              )}
              <div>
                <button type="button" className="ev-ai-proposal__cancel" onClick={() => setProposal(null)} disabled={loading}>
                  Annuler
                </button>
                <button type="button" className="ev-ai-proposal__confirm" onClick={confirmAction} disabled={loading}>
                  Confirmer
                </button>
              </div>
            </section>
          )}

          {loading && (
            <div className="ev-ai-panel__typing">
              <span /><span /><span />
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form className="ev-ai-panel__composer" onSubmit={send}>
          {!isOnline && (
            <div className="ev-ai-panel__offline"><FeatherIcon icon="wifi-off" size={14} /> Connexion Internet requise</div>
          )}
          <div>
            <textarea
              rows="2"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                  send(event);
                }
              }}
              maxLength={MAX_MESSAGE_LENGTH}
              placeholder={mode === "act" ? "Décrivez l’action à préparer…" : "Écrivez votre demande…"}
              aria-label="Votre demande à Evivesoma IA"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={input.trim().length < MIN_MESSAGE_LENGTH || loading || !isOnline}
              aria-label="Envoyer"
            >
              <FeatherIcon icon="arrow-up" size={18} />
            </button>
          </div>
          <small>
            <FeatherIcon icon="lock" size={12} />
            Evivesoma applique vos permissions à chaque demande.
            {input.length > 3600 && ` ${input.length}/${MAX_MESSAGE_LENGTH}`}
          </small>
        </form>
      </aside>
    </>
  );
}

export default EvivesomaAIPanel;
